"use client";

import { useCallback, useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { hotelSearchService } from "@/services/hotelServices/search";
import { getClientSession } from "@/utils/sakaClientSessionHelper";

/**
 * Otel favorileri için global hook.
 * Search, Landing ve Detail sayfalarında kullanılır.
 *
 * @param {Object} options
 * @param {Object} options.hotelData - Otel verisi (search/landing/detail yapısına göre)
 * @param {Function} options.getPayload - FavoritedHotel payload oluşturan fonksiyon (buildFromSearchData, buildFromLandingData, buildFromDetailData)
 * @param {Object} options.context - getPayload'a geçilecek context (pathname, searchParams, locale, currency)
 * @param {string|number} [options.hotelIdKey] - hotelData değiştiğinde state resetlemek için kullanılan otel ID alanı
 */
export function useHotelFavorite({
  hotelData,
  getPayload,
  context = {},
  hotelIdKey = "id",
}) {
  const { isLogin, showLoginModal } = useAuth();
  const [isAddingFavorite, setIsAddingFavorite] = useState(false);
  const [isFavorite, setIsFavorite] = useState(hotelData?.IsFavorited ?? hotelData?.isFavorited ?? false);
  const [favoriteId, setFavoriteId] = useState(hotelData?.FavoriteId ?? hotelData?.favoriteId ?? null);

  const currentHotelId =
    hotelData?.HotelId ?? hotelData?.Id ?? hotelData?.[hotelIdKey] ?? hotelData?.id;

  // Otel değiştiğinde (HotelId) state'i DB'den gelen değerle güncelle
  useEffect(() => {
    if (currentHotelId) {
      setIsFavorite(hotelData?.IsFavorited ?? hotelData?.isFavorited ?? false);
      setFavoriteId(hotelData?.FavoriteId ?? hotelData?.favoriteId ?? null);
    }
  }, [currentHotelId, hotelData?.IsFavorited, hotelData?.isFavorited, hotelData?.FavoriteId, hotelData?.favoriteId]);

  const handleFavoriteClick = useCallback(
    async (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isAddingFavorite) return;

      if (!isLogin) {
        showLoginModal();
        return;
      }

      const sessionData = getClientSession();
      const headers = {};
      if (sessionData?.SessionId) headers["Turna-Session-Id"] = sessionData.SessionId;
      if (sessionData?.SessionToken) headers["Turna-Session-Token"] = sessionData.SessionToken;

      if (!sessionData?.SessionId || !sessionData?.SessionToken) {
        showLoginModal();
        return;
      }

      try {
        setIsAddingFavorite(true);

        if (isFavorite) {
          if (!favoriteId) return;
          await hotelSearchService.removeFavorite(favoriteId, headers, true);
          setIsFavorite(false);
          setFavoriteId(null);
          return;
        }

        if (typeof getPayload !== "function") return;

        const favoritedHotel = getPayload(hotelData, context);
        if (!favoritedHotel?.HotelId) return;

        const response = await hotelSearchService.addFavorite(favoritedHotel, headers, true);
        const newFavoriteId = response?.data?.dbId ?? response?.dbId ?? null;
        setIsFavorite(true);
        if (newFavoriteId) setFavoriteId(newFavoriteId);
      } catch (error) {
        if (error?.response?.status === 401) {
          showLoginModal();
        }
      } finally {
        setIsAddingFavorite(false);
      }
    },
    [
      hotelData,
      getPayload,
      context,
      isLogin,
      showLoginModal,
      isFavorite,
      favoriteId,
      isAddingFavorite,
    ]
  );

  return { isFavorite, favoriteId, isAddingFavorite, handleFavoriteClick };
}
